import { useMutation } from "@tanstack/react-query";
import { Button, Form, Input, Radio, message } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import PhoneInputWithCountrySelect, {
  parsePhoneNumber,
} from "react-phone-number-input";
import { addClient, updateClient } from "../../network/clients";
import queryClient from "../../services/react-query/queryClient";
import { queryKeys } from "../../services/react-query/queryKeys";
import GenderSwitch from "../common/GenderSwitch";
import UserAppointments from "./UserAppointments";

const PopAddNewClient = ({
  close,
  dataClientModal,
  showAppointment = false,
}: any) => {
  const intl = useIntl();
  const [active, setActive] = useState(dataClientModal?.gender || "");
  const [activeStaff, setActiveStaff] = useState(
    dataClientModal?.preferred_staff_gender || ""
  );
  useEffect(() => {
    setActive(dataClientModal?.gender || "");
    setActiveStaff(dataClientModal?.preferred_staff_gender || "");
  }, [dataClientModal]);
  const [form] = Form.useForm();
  const [value, setValue] = useState<any>("");
  const {
    mutate: onAddClient,
    isLoading: isAddingClient,
    isSuccess: isAddClientSuccess,
    error: addClientError,
  } = useMutation(addClient, {
    onSuccess: (res: any) => {
      if (res?.code === "ERR_BAD_REQUEST") {
        message.error(res?.response?.data?.message);
      } else {
        message.success("Client created successfully");
        queryClient.invalidateQueries([queryKeys.listClient]);
        close();
      }
    },
    onError: (error) => {
      message.error("Failed to add client. Please try again.");
    },
  });
  const { mutate: onUpdateClient, isLoading } = useMutation(
    (params) => updateClient(params),
    {
      onSuccess: (res: any) => {
        if (res?.code === "ERR_BAD_REQUEST") {
          message.error(res?.response?.data?.message);
        } else {
          queryClient.invalidateQueries([queryKeys.listMemberships]);
          message.success("Submit success!");
          close();
        }
      },
      onError: (error) => {
        message.error("حاول مرة اخرى");
      },
    }
  );

  const onFinish = async (values: any) => {
    const phoneNumber = parsePhoneNumber(value || dataClientModal?.phone);

    // Creating an object with form values and additional data
    const clientData = {
      ...values,
      ...(dataClientModal && { id: dataClientModal.id }), // Extracting the phone number from parsedPhoneNumber
      phone_country_code: phoneNumber ? phoneNumber.country : "", // Extracting the phone number from parsedPhoneNumber
      phone: phoneNumber ? phoneNumber.nationalNumber : "", // Extracting the phone number from parsedPhoneNumber
      // You can add more properties to the clientData object if needed
    };

    try {
      if (dataClientModal) {
        await onUpdateClient(clientData);
      } else {
        await onAddClient(clientData);
      }
    } catch {}

    // Handle form submission here
    console.log("Received values:", clientData);
  };

  const changeGender = (e: any) => {
    setActive(e.target.value);
  };
  const changeStaffGender = (e: any) => {
    setActiveStaff(e.target.value);
  };

  return (
    <div className=" p-6 max-lg:p-3 min-w-[50%]">
      <div className="bg-white rounded-lg">
        <div className="grid items-center justify-center w-full text-center">
          <h4 className="text-2xl font-semibold text-color-primary">
            {dataClientModal
              ? intl.formatMessage({
                  id: "editCustomerData",
                  defaultMessage: "تعديل بيانات العميل",
                })
              : intl.formatMessage({
                  id: "addANewCustomer",
                  defaultMessage: "إضافة عميل جديد",
                })}
          </h4>
          <span className="mt-4 text-[#75757E] text-base">
            {dataClientModal
              ? intl.formatMessage({
                  id: "updateCustomerDataInfo",
                  defaultMessage:
                    "قم بتحديث المعلومات التالية لتحديث بيانات العميل في مشروعك",
                })
              : intl.formatMessage({
                  id: "subTitleAddNewCustomer",
                  defaultMessage:
                    "قم بتعبئة المعلومات التالية لإضافة عميل جديد إلى مشروعك",
                })}
          </span>
        </div>
      </div>
      <Form
        name="step1"
        onFinish={onFinish}
        layout="vertical"
        form={form}
        className="grid"
        initialValues={{
          first_name: dataClientModal?.first_name,
          last_name: dataClientModal?.last_name,
          email: dataClientModal?.email,
          phone: dataClientModal?.phone, // Initial phone number
          gender: dataClientModal?.gender, // Initial gender
          StaffGender: dataClientModal?.StaffGender, // Initial staff gender
          comments: dataClientModal?.StaffGender, // Initial comments
        }}
      >
        <div className="w-full gap-4 mt-8 bg-white rounded-lg lg:grid">
          <Form.Item
            label={intl.formatMessage({
              id: "firstName",
            })}
            name="first_name"
            className="w-full !mb-0"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "requiredFirstName",
                }),
              },
            ]}
            required={false}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={intl.formatMessage({
              id: "lastName",
            })}
            name="last_name"
            className="w-full !mb-0"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "requiredFamilyName",
                }),
              },
            ]}
            required={false}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={intl.formatMessage({
              id: "email",
            })}
            name="email"

            // rules={[
            //   {
            //     type: "email",
            //     message: "البريد الالكتروني غير صحيح",
            //   },
            // ]}
          >
            <Input disabled={dataClientModal} />
          </Form.Item>
          <Form.Item
            name="phone"
            label={intl.formatMessage({
              id: "phone",
            })}
            className="w-full max-md:!mb-10 !mb-0"
            hasFeedback
          >
            <PhoneInputWithCountrySelect
              defaultCountry="SA"
              value={value}
              onChange={setValue}
              international
              hasFeedback
              disabled={dataClientModal}
              limitMaxLength={true}
              className={`w-full max-md:!mb-10 !mb-0 ${
                dataClientModal ? "phone-input-disabled" : ""
              }`}
            />
          </Form.Item>

          <Form.Item
            name="gender"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "PleaseSpecifyGender",
                }),
              },
            ]}
            required={false}
            className="col-span-2 w-full !-mt-4"
          >
            <div className="flex items-start justify-between col-span-2 max-lg:flex-col text-start">
              <div className="max-lg:mb-3">
                {intl.formatMessage({
                  id: "gender",
                })}
              </div>

              <Radio.Group
                onChange={changeGender}
                defaultValue={dataClientModal?.gender}
              >
                <div className="flex justify-between gap-2">
                  <GenderSwitch
                    active={
                      "male" === active || "man" === active || "men" === active
                    }
                    gender="men"
                    string={intl.formatMessage({
                      id: "manGender",
                    })}
                  />
                  <GenderSwitch
                    active={"woman" === active || "female" === active}
                    gender="woman"
                    string={intl.formatMessage({
                      id: "womanGender",
                    })}
                  />
                </div>
              </Radio.Group>
            </div>
          </Form.Item>
          <Form.Item
            name="preferred_staff_gender"
            required={false}
            className="col-span-2 w-full  !-mt-6"
          >
            <div className="flex items-center justify-between col-span-2 max-lg:grid">
              <div className="max-lg:mb-3">
                {intl.formatMessage({
                  id: "iWouldLikeToDealWith",
                })}
              </div>
              <Radio.Group
                onChange={changeStaffGender}
                defaultValue={dataClientModal?.preferred_staff_gender}
              >
                <div className="flex justify-between gap-2">
                  <GenderSwitch
                    active={
                      "male" === activeStaff ||
                      "man" === activeStaff ||
                      "men" === activeStaff
                    }
                    gender="man"
                    string={intl.formatMessage({
                      id: "man",
                    })}
                  />
                  <GenderSwitch
                    active={"female" === activeStaff}
                    gender="woman"
                    string={intl.formatMessage({
                      id: "woman",
                    })}
                  />
                </div>
              </Radio.Group>
            </div>
          </Form.Item>
        </div>
        <div className="-mt-2">
          <Form.Item
            name="comments"
            label={
              <>
                <span className="">
                  {intl.formatMessage({
                    id: "comments",
                  })}
                </span>
                <small className="text-xs mx-1 self-center mt-2 text-[#75757E]">
                  (
                  {intl.formatMessage({
                    id: "option",
                  })}
                  )
                </small>
              </>
            }
          >
            <Input.TextArea
              placeholder={intl.formatMessage({
                id: "enter_notes_placeholder",
              })}
            />
          </Form.Item>
          {showAppointment ? <UserAppointments appointments={[]} /> : null}
          <div className="w-full col-span-2">
            <Form.Item className="">
              <div className="flex gap-2">
                <Button
                  className="w-full"
                  type="primary"
                  htmlType="submit"
                  loading={dataClientModal ? isLoading : isAddingClient}
                >
                  {dataClientModal
                    ? intl.formatMessage({
                        id: "Save Changes",
                      })
                    : intl.formatMessage({
                        id: "AddMember",
                      })}
                </Button>
                <Button
                  onClick={close}
                  type="primary"
                  className="btn-primary-not"
                >
                  {intl.formatMessage({
                    id: "cancel",
                  })}
                </Button>
              </div>
            </Form.Item>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default PopAddNewClient;
